// src/lib/storage/pull-sync-service.ts
import { SyncService } from "@/lib/storage/device-sync-service";
import { kvGetSyncFromRemote, kvZoperationsSyncFromRemote } from "@/lib/storage/kv-client";
import { SyncEvent } from "@/lib/storage/types";

/**
 * Result of applying a remote change
 */
interface ApplyChangeResult {
  success: boolean;
  event: SyncEvent;
  error?: string;
}

/**
 * Result of pulling changes
 */
export interface PullSyncResult {
  applied: number;
  failed: ApplyChangeResult[];
  totalEvents: number;
}

/**
 * Result of full sync
 */
export interface FullSyncResult {
  syncedKeys: string[];
  failedKeys: string[];
  totalKeys: number;
}

/**
 * PullSyncService handles downloading and applying remote changes
 * to keep devices up-to-date with the latest data from the cloud
 */
export class PullSyncService {
  /**
   * Pull all changes since device's last sync and apply them locally
   */
  static async pullChanges(
    userId: string,
    lastSync: number | null,
    onProgress?: (progress: {
      current: number;
      total: number;
      event: SyncEvent;
    }) => void,
  ): Promise<PullSyncResult> {
    try {
      // Get all events since last sync
      const events = await SyncService.getChangesSinceLastSync(
        userId,
        lastSync,
      );

      if (events.length === 0) {
        return {
          applied: 0,
          failed: [],
          totalEvents: 0,
        };
      }

      console.log(`📥 Pulling ${events.length} changes for user ${userId}`);

      // Apply each event
      const results: ApplyChangeResult[] = [];
      let successCount = 0;

      for (let i = 0; i < events.length; i++) {
        const event = events[i];

        // Report progress
        onProgress?.({ current: i + 1, total: events.length, event });

        try {
          const success = await this.applyRemoteChange(event);

          if (success) {
            successCount++;
            results.push({ success: true, event });
          } else {
            results.push({
              success: false,
              event,
              error: "Failed to sync key from remote",
            });
          }
        } catch (error) {
          console.error(`Error applying change for ${event.dataKey}:`, error);
          results.push({
            success: false,
            event,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      const failed = results.filter((r) => !r.success);

      console.log(
        `✅ Pull sync complete: ${successCount}/${events.length} succeeded`,
      );

      return {
        applied: successCount,
        failed,
        totalEvents: events.length,
      };
    } catch (error) {
      console.error("Error during pull sync:", error);
      throw new Error("Failed to pull changes");
    }
  }

  /**
   * Pull changes for multiple users (used by coaches to pull athlete changes)
   */
  static async pullChangesForMultipleUsers(
    userIds: string[],
    lastSync: number | null,
    onProgress?: (progress: {
      current: number;
      total: number;
      userId: string;
    }) => void,
  ): Promise<PullSyncResult> {
    try {
      console.log(`📥 Pulling changes for ${userIds.length} users`);

      const allResults: ApplyChangeResult[] = [];
      let totalApplied = 0;
      let totalEvents = 0;

      for (let i = 0; i < userIds.length; i++) {
        const userId = userIds[i];

        onProgress?.({ current: i + 1, total: userIds.length, userId });

        const result = await this.pullChanges(userId, lastSync);
        totalApplied += result.applied;
        totalEvents += result.totalEvents;
        allResults.push(...result.failed);
      }

      console.log(
        `✅ Multi-user pull complete: ${totalApplied}/${totalEvents} total changes applied`,
      );

      return {
        applied: totalApplied,
        failed: allResults,
        totalEvents,
      };
    } catch (error) {
      console.error("Error during multi-user pull sync:", error);
      throw new Error("Failed to pull changes for multiple users");
    }
  }

  /**
   * Perform a full sync - download all data for user
   * Used for new devices or devices offline > 7 days
   */
  static async performFullSync(
    userId: string,
    userRole: "coach" | "athlete",
    onProgress?: (progress: {
      current: number;
      total: number;
      key: string;
    }) => void,
  ): Promise<FullSyncResult> {
    try {
      console.log(`🔄 Starting full sync for ${userRole} ${userId}`);

      // Define key patterns to sync based on role
      const patterns = this.getKeyPatternsForRole(userId, userRole);

      const syncedKeys: string[] = [];
      const failedKeys: string[] = [];

      let current = 0;
      const total = patterns.length;

      // Sync each pattern
      for (const pattern of patterns) {
        current++;
        onProgress?.({ current, total, key: pattern });

        try {
          // For pattern keys, we need to sync the entire sorted set or key
          if (pattern.includes("completed-workouts:")) {
            // Sync sorted set
            await kvZoperationsSyncFromRemote(pattern);
          } else {
            // Sync regular key
            await kvGetSyncFromRemote(pattern);
          }

          syncedKeys.push(pattern);
          console.log(`✅ Synced: ${pattern}`);
        } catch (error) {
          console.error(`❌ Failed to sync ${pattern}:`, error);
          failedKeys.push(pattern);
        }
      }

      console.log(
        `✅ Full sync complete: ${syncedKeys.length}/${patterns.length} keys synced`,
      );

      return {
        syncedKeys,
        failedKeys,
        totalKeys: patterns.length,
      };
    } catch (error) {
      console.error("Error during full sync:", error);
      throw new Error("Failed to perform full sync");
    }
  }

  /**
   * Check if a device requires full sync based on last sync time
   */
  static requiresFullSync(lastSync: number | null): boolean {
    if (!lastSync) return true;

    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    const sevenDaysAgo = Date.now() - SEVEN_DAYS;

    return lastSync < sevenDaysAgo;
  }

  /**
   * Get a summary of pending changes for a user
   */
  static async getPendingChangesSummary(
    userId: string,
    lastSync: number | null,
  ): Promise<{
    total: number;
    byType: Record<string, number>;
    oldestChange: number | null;
    newestChange: number | null;
  }> {
    try {
      const events = await SyncService.getChangesSinceLastSync(
        userId,
        lastSync,
      );

      if (events.length === 0) {
        return {
          total: 0,
          byType: {},
          oldestChange: null,
          newestChange: null,
        };
      }

      // Group by type
      const byType: Record<string, number> = {};
      events.forEach((event) => {
        byType[event.type] = (byType[event.type] || 0) + 1;
      });

      return {
        total: events.length,
        byType,
        oldestChange: events[0] ? Date.now() : null, // Events are sorted by score (timestamp)
        newestChange: events[events.length - 1] ? Date.now() : null,
      };
    } catch (error) {
      console.error("Error getting pending changes summary:", error);
      return {
        total: 0,
        byType: {},
        oldestChange: null,
        newestChange: null,
      };
    }
  }

  /**
   * Apply a single remote change by fetching the data and updating local storage
   */
  private static async applyRemoteChange(event: SyncEvent): Promise<boolean> {
    try {
      console.log(`🔄 Applying change: ${event.action} on ${event.dataKey}`);

      // Use the appropriate sync method based on the fetch operation type
      if (event.fetchOp === "get") {
        // Sync regular key-value
        return await kvGetSyncFromRemote(event.dataKey);
      } else if (event.fetchOp === "zrange") {
        // Sync sorted set
        return await kvZoperationsSyncFromRemote(event.dataKey);
      }

      console.warn(`Unknown fetchOp: ${event.fetchOp}`);
      return false;
    } catch (error) {
      console.error(`Error applying change for ${event.dataKey}:`, error);
      return false;
    }
  }

  /**
   * Get key patterns to sync based on user role
   */
  private static getKeyPatternsForRole(
    userId: string,
    role: "coach" | "athlete",
  ): string[] {
    const patterns: string[] = [];

    if (role === "coach") {
      // Coach-specific data
      patterns.push(`coach:${userId}`);
      patterns.push(`coach:${userId}:athlete-templates`);
      patterns.push(`coach:${userId}:athlete-views`);
      patterns.push(`completed-workouts:${userId}`);
    } else {
      // Athlete-specific data
      patterns.push(`athlete:${userId}:templates`);
      patterns.push(`athlete:${userId}:views`);
      patterns.push(`athlete:${userId}:workouts`);
      patterns.push(`user:${userId}:notes`);
      patterns.push(`completed-workouts:${userId}`);
    }

    // Common patterns for both roles
    patterns.push(`user:${userId}:events`);

    return patterns;
  }
}
